import { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { audits as auditsApi, checklist as checklistApi } from "../lib/api";
import { computeScore } from "../lib/score";
import { Card } from "../components/ui/card";
import SemaforoBadge from "../components/SemaforoBadge";
import { ShieldCheck, Paperclip, AlertCircle, FileX, Clock } from "lucide-react";
import { localizedCategory } from "../lib/i18nContent";

export default function PublicSummaryPage() {
  const { token } = useParams();
  const { t, i18n } = useTranslation();
  const [data, setData] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [error, setError] = useState("");
  const [errorCode, setErrorCode] = useState(0);

  useEffect(() => {
    auditsApi
      .publicView(token)
      .then(setData)
      .catch((e) => {
        setErrorCode(e?.response?.status || 0);
        setError(e?.response?.data?.detail || t("public.unavailable"));
      });
    checklistApi.list().then(setQuestions).catch(() => {});
  }, [token]);

  const score = useMemo(() => {
    if (!data) return null;
    return computeScore(data.responses, questions);
  }, [data, questions]);

  if (error) {
    const expired = errorCode === 410;
    return (
      <div className="min-h-screen bg-white flex items-center justify-center p-6">
        <Card className="canales-card p-10 max-w-md text-center" data-testid="public-error">
          {expired ? (
            <Clock size={36} className="mx-auto text-amber-500" />
          ) : (
            <FileX size={36} className="mx-auto text-stone-400" />
          )}
          <h1 className="font-display text-2xl mt-4">
            {expired ? t("public.expired") : t("public.unavailable")}
          </h1>
          <p className="text-sm text-stone-500 mt-2">{error}</p>
          {expired && (
            <p className="text-xs text-stone-400 mt-2">
              {t("public.expired_hint")}
            </p>
          )}
          <Link to="/" className="brand-button inline-flex mt-6 px-5 h-10 items-center">{t("public.go_home")}</Link>
        </Card>
      </div>
    );
  }

  if (!data || !score) {
    return <div className="p-10 text-stone-500">{t("common.loading")}</div>;
  }

  const { audit, responses, findings, files } = data;

  // Only report findings whose question is still answered with a flagged/negative
  // response. Findings for questions re-answered as passing (Sí / Bueno / etc.)
  // are kept in the operational modules but not shown in this public report.
  const PASSING_RESPONSES = new Set([
    "yes", "cumple", "good", "pass", "compliant", "safe", "na",
    "text", "number", "media", "signature",
  ]);
  const respByQ = {};
  (responses || []).forEach((r) => { respByQ[r.question_id] = r.response; });
  const activeFindings = (findings || []).filter((f) => {
    const r = respByQ[f.question_id];
    if (!r) return true;
    return !PASSING_RESPONSES.has(r);
  });
  return (
    <div className="min-h-screen bg-white" data-testid="public-summary">
      {/* Header */}
      <header className="border-b border-stone-200 bg-white">
        <div className="max-w-5xl mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="w-8 h-8 rounded-lg bg-[#1E3A8A] grid place-items-center text-white">
              <ShieldCheck size={16} />
            </span>
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-stone-500 leading-none">{t("brand")}</div>
              <div className="font-display text-sm leading-tight">{t("public.title")}</div>
            </div>
          </div>
          <span className="pill text-[10px]">{t("public.readonly")}</span>
        </div>
      </header>

      {/* Hero */}
      <section className="hero-night text-white">
        <div className="max-w-5xl mx-auto px-6 lg:px-10 py-12">
          <div className="canales-overline text-stone-300">{t("public.title")}</div>
          <h1 className="font-display text-4xl md:text-5xl mt-2">{audit.store_name}</h1>
          <p className="text-stone-300 text-sm mt-2">
            {audit.distrito} · {audit.ciudad} · {t("audit.auditor_label")}: {audit.auditor_name}
          </p>
          <div className="mt-6 flex items-center gap-3 flex-wrap">
            <SemaforoBadge score={score.score} semaforo={score.semaforo} size="lg" />
            <span className="pill bg-white/10 text-white text-[11px]">
              {audit.status === "closed" ? t("audit.closed") : t("audits_list.in_progress")} ·{" "}
              {(audit.signed_at || audit.closed_at || audit.started_at || "").slice(0, 10)}
            </span>
          </div>
        </div>
      </section>

      <div className="max-w-5xl mx-auto px-6 lg:px-10 py-10 space-y-6">
        {/* AI summary */}
        {audit.ai_summary && (
          <Card className="canales-card p-6">
            <div className="canales-overline">{t("summary.ai_title")}</div>
            <p className="text-stone-800 mt-3 leading-relaxed">{audit.ai_summary}</p>
          </Card>
        )}

        {/* Categories */}
        <Card className="canales-card p-6">
          <div className="canales-overline">{t("summary.category_subtotals")}</div>
          <ul className="mt-4 space-y-2.5" data-testid="public-categories">
            {score.categories.map((c) => (
              <li key={c.category} className="flex items-center justify-between text-sm">
                <span className="text-stone-700">{localizedCategory(c.category, i18n.language)}</span>
                <span className="flex items-center gap-2">
                  <span className="text-stone-500 text-xs">
                    {c.obtained.toFixed(1)}/{c.evaluated.toFixed(1)}
                  </span>
                  <SemaforoBadge score={c.subtotal} semaforo={c.semaforo} />
                </span>
              </li>
            ))}
            {score.categories.length === 0 && (
              <li className="text-sm text-stone-500">{t("common.empty")}</li>
            )}
          </ul>
        </Card>

        {/* Findings */}
        <Card className="canales-card p-6">
          <div className="flex items-center justify-between">
            <div className="canales-overline">{t("findings.title")} ({activeFindings.length})</div>
          </div>
          {activeFindings.length === 0 ? (
            <p className="text-sm text-stone-500 mt-3">{t("findings.no_findings")}</p>
          ) : (
            <ul className="mt-3 divide-y divide-stone-100" data-testid="public-findings">
              {activeFindings.map((f) => {
                const photos = f.photos && f.photos.length > 0 ? f.photos : (f.photo_url ? [{ url: f.photo_url }] : []);
                return (
                  <li key={f.id} className="py-3 flex items-start gap-3">
                    <AlertCircle
                      size={16}
                      className={
                        f.severity === "Alta"
                          ? "text-red-600 mt-0.5"
                          : f.severity === "Media"
                          ? "text-amber-600 mt-0.5"
                          : "text-stone-500 mt-0.5"
                      }
                    />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium">{f.description}</div>
                      <div className="text-xs text-stone-500 mt-0.5">
                        {localizedCategory(f.category, i18n.language)} · {t(`severity.${f.severity}`, f.severity)} · {t(`finding_status.${f.status}`, f.status)}
                        {f.responsible ? ` · ${f.responsible}` : ""}
                        {f.commitment_date ? ` · ${f.commitment_date}` : ""}
                      </div>
                      {f.follow_up_notes && (
                        <div className="text-xs text-stone-600 mt-1.5 bg-stone-50 rounded-md p-2">
                          {f.follow_up_notes}
                        </div>
                      )}
                      {photos.length > 0 && (
                        <div className="grid grid-cols-3 sm:grid-cols-5 gap-2 mt-3">
                          {photos.map((p, i) => (
                            <a
                              key={i}
                              href={p.url}
                              target="_blank"
                              rel="noreferrer"
                              className="aspect-square rounded-md border border-stone-200 overflow-hidden bg-stone-50 hover:scale-[1.02] transition-transform"
                            >
                              <img src={p.url} alt={p.original_filename || `evidencia-${i}`} className="w-full h-full object-cover" />
                            </a>
                          ))}
                        </div>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </Card>

        {/* Files */}
        {files.length > 0 && (
          <Card className="canales-card p-6">
            <div className="canales-overline">{t("summary.attachments")} ({files.length})</div>
            <ul className="mt-3 space-y-2" data-testid="public-files">
              {files.map((f) => (
                <li key={f.id} className="flex items-center justify-between text-sm">
                  <a
                    href={f.url}
                    target="_blank"
                    rel="noreferrer"
                    className="flex items-center gap-2 hover:underline truncate"
                  >
                    <Paperclip size={14} />
                    <span className="truncate">{f.original_filename}</span>
                  </a>
                  <span className="text-xs text-stone-500 shrink-0 ml-3">
                    {(f.size / 1024).toFixed(0)} KB · {f.kind}
                  </span>
                </li>
              ))}
            </ul>
          </Card>
        )}

        {/* Signatures */}
        <Card className="canales-card p-6">
          <div className="canales-overline">{t("public.signatures")}</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
            <div>
              <div className="text-stone-700 font-medium">{audit.gerente_name || "—"}</div>
              <div className="border-t border-stone-200 mt-2 pt-1 text-xs text-stone-500">{t("summary.gerente_name")}</div>
            </div>
            <div>
              <div className="text-stone-700 font-medium">{audit.regional_name || "—"}</div>
              <div className="border-t border-stone-200 mt-2 pt-1 text-xs text-stone-500">{t("summary.regional_name")}</div>
            </div>
          </div>
          {audit.signed_at && (
            <div className="text-xs text-stone-500 mt-4">
              {t("public.signed_at", { when: new Date(audit.signed_at).toLocaleString() })}
            </div>
          )}
        </Card>
      </div>

      <footer className="border-t border-stone-200 py-6 text-center text-xs text-stone-500">
        © {t("brand")} · {t("public.title")}
      </footer>
    </div>
  );
}
